import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { TabView, TabPanel } from "primereact/tabview";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { FileUpload } from "primereact/fileupload";
import Layout from "../component/layout";
import HandoverInspectionTab from "../unit_management/HandoverInspectionTab";
import { getItem } from "../../../utils/localStorage";
import { makeRequest2 } from "../../../utils/makeRequest";
import {
  create_move_out_handover,
  update_handover,
  getCustomersURL,
  get_all_handovers,
  get_handover,
  getUnpaidInvoicesForUnit,
  getUnitInvoiceTransactionsURL,
  getFacilityInvoices,
  get_leases,
  GET_CURRENCIES_URL,
  upload_handover_images,
  getUnitMeterHistory,
  get_units_with_move_in,
  get_unit_inspection_items,
  backend_url
} from "../../../utils/urls";

const CreateMoveOutHandover = ({ isDialog = false, onSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [units, setUnits] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [moveInHandovers, setMoveInHandovers] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [withMoveIn, setWithMoveIn] = useState(true);
  const [defaultCurrency, setDefaultCurrency] = useState(null);
  const [currencies, setCurrencies] = useState([]);

  // Image viewer state
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [selectedItemImages, setSelectedItemImages] = useState([]);
  const [selectedItemName, setSelectedItemName] = useState("");

  // Smart meter state
  const [waterMeterData, setWaterMeterData] = useState(null);
  const [powerMeterData, setPowerMeterData] = useState(null);
  const [meterDataLoading, setMeterDataLoading] = useState(false);
  const [previousReadings, setPreviousReadings] = useState({
    water: [],
    electricity: []
  });

  // Comprehensive inventory management state
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showInspectionTab, setShowInspectionTab] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [tempItem, setTempItem] = useState({
    name: "",
    category: "",
    description: "",
    condition: "Good",
    quantity: 1,
    serialNumber: "",
    notes: "",
    images: [],
    repairCost: 0,
    requiresRepair: false,
    currencyId: null,
    currency: null
  });
  const [showDeductionDialog, setShowDeductionDialog] = useState(false);
  const [tempDeduction, setTempDeduction] = useState({
    reason: "",
    amount: 0,
    description: "",
    currencyId: null,
    currency: null
  });

  // unit auto select from customer deactivation
  const [isAutoSelecting, setIsAutoSelecting] = useState(false);
  const { unitId: passedUnitId, customerId: passedCustomerId } = location.state || {};
  const hasAutoSelectTriggered = useRef(false);

  // Inventory item options (matching handover schema enum)
  const categoryOptions = [
    { label: 'Furniture', value: 'Furniture' },
    { label: 'Appliance', value: 'Appliance' },
    { label: 'Electronics', value: 'Electronics' },
    { label: 'Fixture', value: 'Fixture' },
    { label: 'Utility', value: 'Utility' },
    { label: 'Other', value: 'Other' }
  ];

  const conditionOptions = [
    { label: 'Excellent', value: 'Excellent' },
    { label: 'Good', value: 'Good' },
    { label: 'Fair', value: 'Fair' },
    { label: 'Poor', value: 'Poor' },
    { label: 'Damaged', value: 'Damaged' },
    { label: 'Non-functional', value: 'Non-functional' }
  ];

  // Form data
  const [handoverData, setHandoverData] = useState({
    unitId: "",
    customerId: "",
    relatedHandoverId: "",
    handoverDate: new Date(),
    items: [],
    meterReadings: {
      electricity: { reading: 0 },
      water: { reading: 0 },
      gas: { reading: 0 }
    },
    keysHandedOver: 0,
    securityDeposit: {
      amount: 0,
      deductions: [],
      refundAmount: 0
    },
    notes: "",
    attachments: [],
    status: "Draft"
  });

  // Fetch currencies for this facility
  const fetchCurrencies = async (facilityIdValue) => {
    try {
      const response = await makeRequest2(
        `${GET_CURRENCIES_URL}/${facilityIdValue}`,
        "GET"
      );

      if (response.success && response.data) {
        const currencyData = response.data.data || response.data;
        const currenciesArray = Array.isArray(currencyData)
          ? currencyData
          : [currencyData];

        setCurrencies(currenciesArray);

        // Find default currency
        const defaultCurr = currenciesArray.find(curr => curr.isDefaultCurrency);
        if (defaultCurr) {
          setDefaultCurrency(defaultCurr);
        } else if (currenciesArray.length > 0) {
          // Use first currency as fallback
          setDefaultCurrency(currenciesArray[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching currencies:", error);
    }
  };

  // Fetch smart meter data for water
  const fetchWaterMeterData = async (unitId) => {
    try {
      setMeterDataLoading(true);
      const response = await makeRequest2(`${getUnitMeterHistory}/${facilityId}/${unitId}`, 'GET');

      if (response && response.success && response.data && response.data.meter) {
        setWaterMeterData(response.data.meter);

        // Auto-fill current reading if available
        if (response.data.meter.currentReading) {
          handleMeterReadingChange('water', 'reading', response.data.meter.currentReading);
        }

        toast.current.show({
          severity: 'success',
          summary: 'Smart Water Meter Found',
          detail: 'Water meter reading automatically fetched',
          life: 3000
        });
      } else {
        setWaterMeterData(null);
      }
    } catch (error) {
      console.error('Error fetching water meter data:', error);
      setWaterMeterData(null);
    } finally {
      setMeterDataLoading(false);
    }
  };

  // Fetch smart meter data for power
  const fetchPowerMeterData = async (unitId) => {
    try {
      setMeterDataLoading(true);
      const response = await makeRequest2(
        `/api/app/power_meter_management/controllers/power_meters/get_unit_meter/${unitId}`,
        'GET'
      );

      if (response && response.success && response.data.data) {
        setPowerMeterData(response.data.data);

        // Auto-fill current reading if available
        if (response.data.data.currentReading) {
          handleMeterReadingChange('electricity', 'reading', response.data.data.currentReading);
        }

        toast.current.show({
          severity: 'success',
          summary: 'Smart Power Meter Found',
          detail: 'Power meter reading automatically fetched',
          life: 3000
        });
      } else {
        setPowerMeterData(null);
      }
    } catch (error) {
      console.error('Error fetching power meter data:', error);
      setPowerMeterData(null);
    } finally {
      setMeterDataLoading(false);
    }
  };

  // Fetch previous readings from past handovers
  const fetchPreviousReadings = async (unitId) => {
    try {
      const response = await makeRequest2(`${get_all_handovers}/${facilityId}`, "GET");

      if (response.success && response.data) {
        let handoversList = [];

        if (response.data.data && Array.isArray(response.data.data)) {
          handoversList = response.data.data;
        } else if (Array.isArray(response.data)) {
          handoversList = response.data;
        } else if (response.data.handovers && Array.isArray(response.data.handovers)) {
          handoversList = response.data.handovers;
        }

        // Filter handovers for this unit
        const unitHandovers = handoversList.filter(handover => {
          const handoverUnitId = typeof handover.unitId === 'object' ? handover.unitId?._id : handover.unitId;
          return String(handoverUnitId) === String(unitId);
        });

        // Extract meter readings from past handovers
        const waterReadings = [];
        const electricityReadings = [];

        unitHandovers.forEach(handover => {
          if (handover.meterReadings) {
            if (handover.meterReadings.water && handover.meterReadings.water.reading) {
              waterReadings.push({
                reading: handover.meterReadings.water.reading,
                date: handover.handoverDate || handover.createdAt,
                type: handover.handoverType || 'Unknown'
              });
            }
            if (handover.meterReadings.electricity && handover.meterReadings.electricity.reading) {
              electricityReadings.push({
                reading: handover.meterReadings.electricity.reading,
                date: handover.handoverDate || handover.createdAt,
                type: handover.handoverType || 'Unknown'
              });
            }
          }
        });

        // Sort by date (most recent first)
        waterReadings.sort((a, b) => new Date(b.date) - new Date(a.date));
        electricityReadings.sort((a, b) => new Date(b.date) - new Date(a.date));

        setPreviousReadings({
          water: waterReadings.slice(0, 5), // Keep only last 5 readings
          electricity: electricityReadings.slice(0, 5)
        });
      }
    } catch (error) {
      console.error('Error fetching previous readings:', error);
    }
  };

  // Load initial data with retry logic
  useEffect(() => {
    const fetchInitialData = async (retryCount = 0) => {
      const maxRetries = 3;
      try {
        setLoading(true);
        const facilityIdValue = await getItem("selectedFacilityId");
        setFacilityId(facilityIdValue);

        if (!facilityIdValue) {
          toast.current.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Facility ID not found',
            life: 3000
          });
          return;
        }

        const handoverUrl = `${get_all_handovers}/${facilityIdValue}`;

        const [unitsResponse, customersResponse, handoversResponse] = await Promise.all([
          makeRequest2(`${get_units_with_move_in}/${facilityIdValue}`, "GET", null, { retries: 2 }),
          makeRequest2(`${getCustomersURL}/${facilityIdValue}`, "GET", null, { retries: 2 }),
          makeRequest2(handoverUrl, "GET", null, { retries: 2 }),
          fetchCurrencies(facilityIdValue)
        ]);

        console.log("unitsResponse:", unitsResponse);

        // Validate responses
        let hasErrors = false;
        const errors = [];

        if (unitsResponse.success && unitsResponse.data) {
          // Handle both nested and direct data structures
          let unitsList = [];
          if (unitsResponse.data.data && Array.isArray(unitsResponse.data.data)) {
            unitsList = unitsResponse.data.data;
          } else if (Array.isArray(unitsResponse.data)) {
            unitsList = unitsResponse.data;
          }

          setUnits(unitsList);
        } else {
          hasErrors = true;
          errors.push('Failed to load units');
        }

        if (customersResponse.success && Array.isArray(customersResponse.data)) {
          const activeCustomers = customersResponse.data.filter(customer =>
            customer && customer.status === 'Active' && customer.customerType === 'tenant'
          );
          setCustomers(activeCustomers);
        } else {
          hasErrors = true;
          errors.push('Failed to load customers');
        }

        if (handoversResponse.success && handoversResponse.data) {
          let handoversList = [];

          // Robust handling of nested data structure
          try {
            if (handoversResponse.data.data && Array.isArray(handoversResponse.data.data)) {
              handoversList = handoversResponse.data.data;
            } else if (Array.isArray(handoversResponse.data)) {
              handoversList = handoversResponse.data;
            } else if (handoversResponse.data.handovers && Array.isArray(handoversResponse.data.handovers)) {
              handoversList = handoversResponse.data.handovers;
            } else {
              console.warn('Unexpected handovers response structure:', handoversResponse.data);
              handoversList = [];
            }
          } catch (parseError) {
            console.error('Error parsing handovers response:', parseError);
            handoversList = [];
          }

          // Validate and filter for move-in handovers
          const moveInHandoversList = handoversList.filter(handover => {
            if (!handover || typeof handover !== 'object') return false;

            // Check for handoverType variations (case-insensitive)
            const handoverType = handover.handoverType || handover.type;
            if (!handoverType) return false;

            const typeStr = handoverType.toString().toLowerCase();
            return typeStr.includes('movein') || typeStr.includes('move-in') || typeStr.includes('move_in');
          });

          console.log('Debug: All handovers loaded:', handoversList.length);
          console.log('Debug: Move-in handovers filtered:', moveInHandoversList.length);
          console.log('Debug: Sample handover types:', handoversList.slice(0, 3).map(h => h.handoverType || h.type));
          setMoveInHandovers(moveInHandoversList);

          // Show feedback
          if (moveInHandoversList.length === 0 && handoversList.length > 0) {
            toast.current.show({
              severity: 'info',
              summary: 'No Move-In Handovers',
              detail: 'No completed move-in handovers found for inventory loading',
              life: 4000
            });
          }
        } else {
          hasErrors = true;
          errors.push(`Failed to load handovers: ${handoversResponse.error || 'Unknown error'}`);
          console.log('Debug: Failed to load handovers:', handoversResponse);
        }

        // Show partial success warnings
        if (hasErrors && errors.length > 0) {
          const errorMsg = errors.join(', ');

          // Retry on network-related errors
          if (retryCount < maxRetries && errorMsg.includes('No response received')) {
            console.log(`Retrying initial data fetch (attempt ${retryCount + 1}/${maxRetries})`);
            setTimeout(() => fetchInitialData(retryCount + 1), 2000 * (retryCount + 1));
            return;
          }

          toast.current.show({
            severity: 'warn',
            summary: 'Partial Load Error',
            detail: errorMsg,
            life: 5000
          });
        }
      } catch (error) {
        console.error('Error in fetchInitialData:', error);

        const errorMsg = error.message || 'Error loading initial data';

        // Retry on network errors
        if (retryCount < maxRetries && (errorMsg.includes('Network Error') || errorMsg.includes('timeout'))) {
          console.log(`Retrying initial data fetch due to error (attempt ${retryCount + 1}/${maxRetries})`);
          setTimeout(() => fetchInitialData(retryCount + 1), 2000 * (retryCount + 1));
          return;
        }

        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: errorMsg,
          life: 5000
        });
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Render handover header
  const renderHandoverHeader = () => {
    const selectedUnit = units.find(u => u._id === handoverData.unitId);
    const selectedCustomer = customers.find(c => c._id === handoverData.customerId);

    if (!selectedUnit && !selectedCustomer) return null;

    return (
      <div className="card mb-4 border-0 shadow-sm">
        <div className="card-body p-4">
          <div className="row g-4">
            <div className="col-md-3">
              <div className="d-flex align-items-center mb-2">
                <i className="ti ti-home-minus text-danger me-2" style={{ fontSize: '1.2rem' }}></i>
                <h6 className="mb-0 text-danger fw-bold">Move-Out Handover</h6>
              </div>
              <div className="d-flex align-items-center">
                <span className={`badge ${handoverData.status === 'Completed' ? 'bg-success' :
                  handoverData.status === 'Draft' ? 'bg-warning' : 'bg-secondary'
                  } me-2`}>
                  {handoverData.status || 'Draft'}
                </span>
                <small className="text-muted">
                  {handoverData.handoverDate ? handoverData.handoverDate.toLocaleDateString() : 'Today'}
                </small>
              </div>
            </div>

            {selectedCustomer && (
              <div className="col-md-4">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-user text-success me-2" style={{ fontSize: '1.2rem' }}></i>
                  <h6 className="mb-0 text-success fw-bold">Customer Details</h6>
                </div>
                <div>
                  <div className="fw-medium text-dark">
                    {`${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim()}
                  </div>
                  {selectedCustomer.email && (
                    <div className="small text-muted">
                      <i className="ti ti-mail me-1"></i>
                      {selectedCustomer.email}
                    </div>
                  )}
                  {selectedCustomer.phoneNumber && (
                    <div className="small text-muted">
                      <i className="ti ti-phone me-1"></i>
                      {selectedCustomer.phoneNumber}
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedUnit && (
              <div className="col-md-5">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-building text-info me-2" style={{ fontSize: '1.2rem' }}></i>
                  <h6 className="mb-0 text-info fw-bold">Unit Information</h6>
                </div>
                <div className="row">
                  <div className="col-6">
                    <div className="fw-medium text-dark">{selectedUnit.name || 'Unknown Unit'}</div>
                    <div className="small text-muted">
                      <i className="ti ti-map-pin me-1"></i>
                      {selectedUnit.floorUnitNo || 'N/A'}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="small text-muted">
                      <i className="ti ti-circle-check me-1"></i>
                      {selectedUnit.status || 'Active'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };


  // Event handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setHandoverData(prev => ({ ...prev, [name]: value }));
  };

  const handleMeterReadingChange = (utility, field, value) => {
    setHandoverData(prev => ({
      ...prev,
      meterReadings: {
        ...prev.meterReadings,
        [utility]: {
          ...prev.meterReadings[utility],
          [field]: value
        }
      }
    }));
  };

  // Auto-select unit when units are loaded and unitId is passed // auto select
  useEffect(() => {
    if (hasAutoSelectTriggered.current) return;

    if (passedUnitId && passedCustomerId && !handoverData.unitId) {
      hasAutoSelectTriggered.current = true;
      setIsAutoSelecting(true);

      // Directly set the data from state
      setHandoverData(prev => ({
        ...prev,
        unitId: passedUnitId,
        customerId: passedCustomerId,
        moveInHandoverId: location.state?.moveInHandoverId || null
      }));

      // Load all the necessary data
      Promise.all([
        loadMoveInInventoryItems(passedUnitId, passedCustomerId, location.state?.moveInHandoverId),
        loadLeaseDepositAndUnpaidInvoices(passedUnitId, passedCustomerId),
        fetchWaterMeterData(passedUnitId),
        fetchPowerMeterData(passedUnitId),
        fetchPreviousReadings(passedUnitId)
      ]).finally(() => {
        setIsAutoSelecting(false);
      });
    }
  }, [passedUnitId, passedCustomerId]); // Don't depend on units array

  // updated function with the autoselect
  const handleUnitSelect = async (unitId) => {
    const selectedUnit = units.find(unit => unit._id === unitId);
    if (selectedUnit) {
      // Use passedCustomerId if available, otherwise use the unit's customerId
      const customerId = passedCustomerId || selectedUnit.customerId;
      const moveInHandoverId = selectedUnit.moveInHandoverId;

      // Unit is valid - proceed with selection
      setHandoverData(prev => ({
        ...prev,
        unitId: unitId,
        customerId: customerId,
        moveInHandoverId: moveInHandoverId
      }));

      // Load move-in inventory items for this unit
      await loadMoveInInventoryItems(unitId, customerId, moveInHandoverId);

      // Load lease deposit and unpaid invoices
      await loadLeaseDepositAndUnpaidInvoices(unitId, customerId);

      // Fetch smart meter data for this unit
      fetchWaterMeterData(unitId);
      fetchPowerMeterData(unitId);
      fetchPreviousReadings(unitId);

      return true;
    }
    return false;
  };

  const handleManualUnitSelect = async (e) => {
    const unitId = e.value;
    setIsAutoSelecting(true);
    await handleUnitSelect(unitId);
    setIsAutoSelecting(false);
  };

  const loadMoveInInventoryItems = async (unitId, customerId, moveInHandoverId) => {
    try {
      setInventoryLoading(true);

      // Validate input parameters
      if (!unitId || !customerId || !moveInHandoverId) {
        toast.current.show({
          severity: 'error',
          summary: 'Invalid Parameters',
          detail: 'Unit ID, Customer ID and Move-In Handover ID are required to load inventory items.',
          life: 3000
        });
        return;
      }

      console.log('Debug: Fetching move-in handover:', moveInHandoverId);

      // Fetch the specific move-in handover by ID
      const handoverResponse = await makeRequest2(
        `${get_handover}/${facilityId}/${moveInHandoverId}`,
        "GET",
        null,
        { retries: 2 }
      );

      console.log('Debug: Move-in handover response:', handoverResponse);

      // Handle nested response structure
      let moveInHandover = null;
      if (handoverResponse.success && handoverResponse.data) {
        if (handoverResponse.data.data) {
          moveInHandover = handoverResponse.data.data;
        } else {
          moveInHandover = handoverResponse.data;
        }
      }

      if (!moveInHandover) {
        toast.current.show({
          severity: 'error',
          summary: 'Handover Not Found',
          detail: 'Could not load the move-in handover data.',
          life: 4000
        });
        return;
      }

      // Validate items array
      if (!moveInHandover.items || !Array.isArray(moveInHandover.items) || moveInHandover.items.length === 0) {
        toast.current.show({
          severity: 'info',
          summary: 'No Inventory Items',
          detail: 'The move-in handover has no inventory items. Please add inspection items in Unit Management.',
          life: 4000
        });
        return;
      }

      // Convert move-in items to move-out format
      const moveOutItems = moveInHandover.items
        .filter(item => {
          if (!item || typeof item !== 'object') return false;
          if (!item.name && !item.description) return false;
          return true;
        })
        .map(item => {
          try {
            const cleanItem = { ...item };
            if (typeof cleanItem._id === 'number') delete cleanItem._id;
            if (typeof cleanItem.id === 'number') delete cleanItem.id;

            return {
              ...cleanItem,
              originalCondition: item.condition || 'Good',
              currentCondition: item.condition || 'Good',
              repairCost: 0,
              repairDescription: '',
              requiresRepair: false,
              currencyId: item.currencyId || null,
              moveInHandoverId: moveInHandover._id,
              moveInItemId: item._id || item.id,
              isFromMoveIn: true  // Mark as coming from move-in handover
            };
          } catch (itemError) {
            console.error('Error processing item:', item, itemError);
            return null;
          }
        })
        .filter(item => item !== null);

      if (moveOutItems.length === 0) {
        toast.current.show({
          severity: 'warn',
          summary: 'No Valid Items',
          detail: 'No valid inventory items could be loaded from the move-in handover.',
          life: 4000
        });
        return;
      }

      // Update handover data with loaded items
      setHandoverData(prev => ({
        ...prev,
        items: moveOutItems,
        relatedHandoverId: moveInHandover._id
      }));

      toast.current.show({
        severity: 'success',
        summary: 'Inventory Loaded',
        detail: `${moveOutItems.length} item(s) loaded from move-in handover`,
        life: 3000
      });

    } catch (error) {
      console.error('Error loading move-in inventory:', error);

      const errorMessage = error.message || 'Failed to load move-in inventory items';

      toast.current.show({
        severity: 'error',
        summary: 'Inventory Load Error',
        detail: `${errorMessage}. Please check your connection and try again.`,
        life: 5000
      });

      setHandoverData(prev => ({
        ...prev,
        items: [],
        relatedHandoverId: null
      }));
    } finally {
      setInventoryLoading(false);
    }
  };

  // Load lease deposit and unpaid invoices
  const loadLeaseDepositAndUnpaidInvoices = async (unitId, customerId) => {
    let depositAmount = 0;
    let invoiceDeductions = [];

    // First, try to fetch the lease deposit (this is the priority)
    try {
      console.log('Fetching leases for facility:', facilityId);
      const leasesResponse = await makeRequest2(
        `${get_leases}/${facilityId}`,
        "GET"
      );

      if (leasesResponse.success && leasesResponse.data) {
        console.log('All leases data:', leasesResponse.data);

        // Get leases array from the response (it's nested under leaseAgreements)
        const leasesArray = leasesResponse.data.leaseAgreements || leasesResponse.data;
        console.log('Leases array:', leasesArray);

        // Find active lease for this unit and customer
        const activeLease = leasesArray.find(lease => {
          // Handle both populated objects and string IDs for unitNumber field
          const leaseUnitId = typeof lease.unitNumber === 'object' ? lease.unitNumber._id : lease.unitNumber;
          // Handle both populated objects and string IDs for tenant field  
          const leaseTenantId = typeof lease.tenant === 'object' ? lease.tenant._id : lease.tenant;

          console.log('Comparing lease:', {
            leaseId: lease._id,
            leaseUnitId,
            leaseTenantId,
            leaseStatus: lease.status,
            targetUnitId: unitId,
            targetCustomerId: customerId
          });

          return leaseUnitId === unitId &&
            leaseTenantId === customerId &&
            lease.status === 'Active';
        });

        if (activeLease && activeLease.financialTerms && activeLease.financialTerms.securityDeposit) {
          depositAmount = parseFloat(activeLease.financialTerms.securityDeposit) || 0;
          console.log('Found active lease with security deposit:', depositAmount);
        } else {
          console.log('No active lease with security deposit found for this unit and customer');
          console.log('Active lease found:', activeLease);
        }
      } else {
        console.log('Failed to fetch leases or no data returned:', leasesResponse);
      }
    } catch (leaseError) {
      console.warn('Error fetching lease data:', leaseError);
    }

    // Second, try to fetch unpaid invoices (separate from lease deposit)
    try {
      console.log('Fetching invoices for unit:', unitId);
      let allInvoices = [];

      // Try multiple endpoints to find invoices
      // Method 1: Try unit-specific invoice endpoint
      try {
        const unitInvoicesResponse = await makeRequest2(
          `${getUnitInvoiceTransactionsURL}/${facilityId}/${unitId}`,
          "GET"
        );
        console.log('Unit invoices response:', unitInvoicesResponse);

        if (unitInvoicesResponse.success && unitInvoicesResponse.data) {
          allInvoices = Array.isArray(unitInvoicesResponse.data) ? unitInvoicesResponse.data :
            unitInvoicesResponse.data.invoices || [];
        }
      } catch (unitInvoiceError) {
        console.warn('Unit invoice endpoint failed:', unitInvoiceError);
      }

      // Method 2: If unit invoices failed, try facility invoices and filter
      if (allInvoices.length === 0) {
        try {
          const facilityInvoicesResponse = await makeRequest2(
            `${getFacilityInvoices}/${facilityId}`,
            "GET"
          );
          console.log('Facility invoices response:', facilityInvoicesResponse);

          if (facilityInvoicesResponse.success && facilityInvoicesResponse.data) {
            const facilityInvoices = Array.isArray(facilityInvoicesResponse.data) ? facilityInvoicesResponse.data :
              facilityInvoicesResponse.data.invoices || [];

            // Filter invoices for this specific unit
            allInvoices = facilityInvoices.filter(invoice => {
              const invoiceUnitId = typeof invoice.unit?.id === 'string' ? invoice.unit.id :
                typeof invoice.unit === 'object' ? invoice.unit._id :
                  typeof invoice.unitId === 'object' ? invoice.unitId._id :
                    invoice.unitId;
              return invoiceUnitId === unitId;
            });
          }
        } catch (facilityInvoiceError) {
          console.warn('Facility invoice endpoint failed:', facilityInvoiceError);
        }
      }

      console.log('All invoices found:', allInvoices);

      if (allInvoices.length > 0) {
        // Filter invoices with status other than "Paid" and exclude property management invoices
        const unpaidInvoices = allInvoices.filter(invoice =>
          invoice.status && invoice.status.toLowerCase() !== 'paid' &&
          invoice.whatFor?.invoiceType !== 'Property-Management'
        );

        console.log('Filtered unpaid invoices:', unpaidInvoices);

        // Create deductions for each unpaid invoice (only if there's an outstanding amount)
        invoiceDeductions = unpaidInvoices
          .map(invoice => {
            const totalAmount = parseFloat(invoice.totalAmount) || 0;
            const amountPaid = parseFloat(invoice.amountPaid) || 0;
            const outstandingAmount = totalAmount - amountPaid;

            return {
              reason: `Invoice ${invoice.invoiceNumber}`,
              amount: outstandingAmount,
              description: `Outstanding invoice (${invoice.status}) for ${invoice.whatFor?.description || invoice.whatFor?.invoiceType || 'services'} - Due: ${new Date(invoice.dueDate).toLocaleDateString()}`
            };
          })
          .filter(deduction => deduction.amount > 0); // Only include invoices with outstanding amounts

        console.log('Invoice deductions created:', invoiceDeductions);
      } else {
        console.log('No invoices found for this unit');
      }
    } catch (invoiceError) {
      console.warn('Error fetching invoices (will continue without them):', invoiceError);
    }

    // Update handover data with deposit and invoice deductions
    setHandoverData(prev => ({
      ...prev,
      securityDeposit: {
        ...prev.securityDeposit,
        amount: depositAmount,
        deductions: [...prev.securityDeposit.deductions, ...invoiceDeductions]
      }
    }));

    // Show success messages
    if (depositAmount > 0) {
      toast.current.show({
        severity: 'success',
        summary: 'Lease Deposit Loaded',
        detail: `Security deposit amount: ${defaultCurrency?.currencyShortCode || '$'}${depositAmount.toFixed(2)}`,
        life: 3000
      });
    } else {
      toast.current.show({
        severity: 'warn',
        summary: 'No Lease Deposit Found',
        detail: 'No active lease or security deposit found. Please enter manually.',
        life: 4000
      });
    }

    if (invoiceDeductions.length > 0) {
      toast.current.show({
        severity: 'info',
        summary: 'Unpaid Invoices Found',
        detail: `${invoiceDeductions.length} unpaid invoice(s) added as deductions. Total: ${defaultCurrency?.currencyShortCode || '$'}${invoiceDeductions.reduce((sum, d) => sum + d.amount, 0).toFixed(2)}`,
        life: 4000
      });
    }
  };

  const handleClear = () => {
    setHandoverData({
      unitId: "",
      customerId: "",
      relatedHandoverId: "",
      handoverDate: new Date(),
      items: [],
      meterReadings: {
        electricity: { reading: 0 },
        water: { reading: 0 },
        gas: { reading: 0 }
      },
      keysHandedOver: 0,
      securityDeposit: {
        amount: 0,
        deductions: [],
        refundAmount: 0
      },
      notes: "",
      attachments: [],
      status: "Draft"
    });
    setActiveTab(0);
  };

  // Function to upload images for an item
  const uploadItemImages = async (images, handoverId) => {
    if (!images || images.length === 0) return [];

    try {
      // Filter only images that need to be uploaded (have 'file' property)
      const imagesToUpload = images.filter(img => img.file);

      if (imagesToUpload.length === 0) {
        // Return existing URLs if no new images to upload
        return images.map(img => typeof img === 'string' ? img : img.preview || img.url);
      }

      const formData = new FormData();
      imagesToUpload.forEach((imageData, index) => {
        formData.append('images', imageData.file, imageData.name);
      });

      const response = await makeRequest2(
        `${upload_handover_images}/${facilityId}/${handoverId}`,
        "POST",
        formData,
        {
          'Content-Type': 'multipart/form-data'
        }
      );

      if (response?.success && response.data?.images) {
        // Combine existing URLs with newly uploaded URLs
        const existingUrls = images.filter(img => !img.file).map(img =>
          typeof img === 'string' ? img : img.preview || img.url
        );
        const newUrls = response.data.images.map(img => img.url);

        return [...existingUrls, ...newUrls];
      } else {
        console.error('Image upload failed:', response?.error);
        throw new Error(response?.error || 'Image upload failed');
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    }
  };

  const uploadAttachments = async (attachments, handoverId) => {
    if (!attachments || attachments.length === 0) return [];

    try {
      const attachmentsToUpload = attachments.filter(att => att.file);

      if (attachmentsToUpload.length === 0) {
        return attachments.map(att => ({
          name: att.name,
          fileName: att.fileName,
          fileUrl: att.fileUrl,
          uploadDate: att.uploadDate
        }));
      }

      const formData = new FormData();
      attachmentsToUpload.forEach((attachment) => {
        formData.append('images', attachment.file, attachment.fileName || attachment.file.name);
      });

      const response = await makeRequest2(
        `${upload_handover_images}/${facilityId}/${handoverId}`,
        "POST",
        formData,
        { 'Content-Type': 'multipart/form-data' }
      );

      if (response?.success && response.data?.images) {
        const uploadedAttachments = attachmentsToUpload.map((att, index) => ({
          name: att.name,
          fileName: att.fileName,
          fileUrl: response.data.images[index]?.url || att.fileUrl,
          uploadDate: att.uploadDate
        }));

        const existingAttachments = attachments
          .filter(att => !att.file)
          .map(att => ({
            name: att.name,
            fileName: att.fileName,
            fileUrl: att.fileUrl,
            uploadDate: att.uploadDate
          }));

        return [...existingAttachments, ...uploadedAttachments];
      } else {
        throw new Error(response?.error || 'Attachment upload failed');
      }
    } catch (error) {
      console.error('Error uploading attachments:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (!handoverData.unitId || !handoverData.customerId || isSubmitting) {
      toast.current.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please select a unit and customer',
        life: 3000
      });
      return;
    }

    // Validate attachment names
    const attachmentsWithoutNames = handoverData.attachments.filter(att => !att.name || att.name.trim() === '');
    if (attachmentsWithoutNames.length > 0) {
      toast.current.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please provide a name for all attachments',
        life: 3000
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Step 1: Create handover without images first
      const cleanedItems = handoverData.items.map(item => {
        const cleanedItem = { ...item };

        // Remove manual IDs (numeric IDs that cause ObjectId cast errors)
        if (typeof cleanedItem._id === 'number' || typeof cleanedItem.id === 'number') {
          delete cleanedItem._id;
          delete cleanedItem.id;
        }

        // Create without images initially
        cleanedItem.images = [];

        return cleanedItem;
      });

      // Clean attachments - remove file objects, keep only metadata
      const cleanedAttachments = handoverData.attachments.map(att => ({
        name: att.name,
        fileName: att.fileName,
        fileUrl: att.fileUrl,
        uploadDate: att.uploadDate
      }));

      const formData = {
        ...handoverData,
        handoverDate: handoverData.handoverDate.toISOString(),
        items: cleanedItems,
        attachments: cleanedAttachments
      };

      if (!withMoveIn) {
        formData.relatedHandoverId = null;
      }

      // Create the handover first
      const response = await makeRequest2(
        `${create_move_out_handover}/${facilityId}`,
        "POST",
        formData
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to create move-out handover');
      }

      const createdHandoverId = response.data?._id;

      // Step 2: Upload images for items that have them
      if (createdHandoverId && handoverData.items.some(item => item.images && item.images.length > 0)) {
        try {
          // Upload images and update items with URLs
          const itemsWithImageUrls = await Promise.all(
            handoverData.items.map(async (item) => {
              const cleanedItem = { ...item };

              // Remove manual IDs
              if (typeof cleanedItem._id === 'number' || typeof cleanedItem.id === 'number') {
                delete cleanedItem._id;
                delete cleanedItem.id;
              }

              // Upload images if they exist
              try {
                if (item.images && item.images.length > 0) {
                  cleanedItem.images = await uploadItemImages(item.images, createdHandoverId);
                } else {
                  cleanedItem.images = [];
                }
              } catch (imageError) {
                console.error('Error uploading images for item:', item.name, imageError);
                cleanedItem.images = [];
              }

              return cleanedItem;
            })
          );

          // Step 3: Update the handover with image URLs
          const updatePayload = {
            items: itemsWithImageUrls
          };

          await makeRequest2(
            `${update_handover}/${facilityId}/${createdHandoverId}`,
            "PUT",
            updatePayload
          );
        } catch (imageError) {
          console.error('Error uploading images:', imageError);
        }
      }

      // Step 4: Upload attachments if there are any
      if (createdHandoverId && handoverData.attachments.length > 0) {
        try {
          const attachmentsWithUrls = await uploadAttachments(handoverData.attachments, createdHandoverId);

          // Update handover with actual attachment URLs
          const attachmentUpdatePayload = {
            attachments: attachmentsWithUrls
          };

          await makeRequest2(
            `${update_handover}/${facilityId}/${createdHandoverId}`,
            "PUT",
            attachmentUpdatePayload
          );
        } catch (attachmentError) {
          console.error('Error uploading attachments:', attachmentError);
        }
      }

      // Show success message
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Move-out handover created successfully',
        life: 3000
      });

      // Navigate or close dialog
      if (isDialog) {
        onSuccess?.();
      } else {
        navigate("/facility/handover_management/handovers", {
          state: { activeTab: 'moveOut' },
          replace: true
        });
      }
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'An unexpected error occurred',
        life: 3000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Tab render functions
  const renderBasicInfoTab = () => (
    <Card className="mb-4">
      <div className="card-title mb-0 p-3 bg-light d-flex align-items-center">
        <i className="ti ti-info-circle text-blue me-2" style={{ fontSize: '1.1rem' }}></i>
        <h5 className="mb-0">Basic Information</h5>
      </div>
      <Divider className="m-0" />
      <div className="p-4">
        <div className="row">

          <div className="col-md-6">
            <div className="mb-3">
              <label htmlFor="unit" className="form-label fw-medium">
                Unit *
                {passedUnitId && handoverData.unitId === passedUnitId && (
                  <span className="text-success ms-2">
                    <i className="pi pi-check-circle"></i> Auto-selected: {location.state?.unitName || 'Unit'}
                  </span>
                )}
                {isAutoSelecting && (
                  <span className="text-warning ms-2">
                    <i className="pi pi-spin pi-spinner"></i> Loading unit data...
                  </span>
                )}
              </label>
              <Dropdown
                id="unit"
                value={handoverData.unitId}
                options={units.map(unit => ({
                  label: `${unit.name} - ${unit.floorUnitNo}`,
                  value: unit._id
                }))}
                onChange={handleManualUnitSelect}
                placeholder={isAutoSelecting ? "Loading unit..." : "Select a unit"}
                filter
                className="w-100"
                disabled={!units.length || isAutoSelecting}
              />

              {handoverData.unitId === passedUnitId && handoverData.customerId && (
                <div className="text-muted small mt-1">
                  <i className="pi pi-info-circle"></i> Customer data loaded automatically
                </div>
              )}
            </div>
          </div>

          <div className="col-md-6">
            <div className="mb-3">
              <label htmlFor="customer" className="form-label fw-medium">Customer (Auto-selected)</label>
              <InputText
                id="customer"
                value={handoverData.customerId ?
                  `${customers.find(c => c._id === handoverData.customerId)?.firstName || ''} ${customers.find(c => c._id === handoverData.customerId)?.lastName || ''}`.trim() || 'No customer selected' :
                  'No customer selected'
                }
                disabled
                placeholder="Customer will be auto-selected when unit is chosen"
                className="w-100"
              />
            </div>
          </div>

          <div className="col-md-6">
            <div className="mb-3">
              <label htmlFor="handoverDate" className="form-label fw-medium">Handover Date *</label>
              <Calendar
                id="handoverDate"
                value={handoverData.handoverDate}
                onChange={(e) => handleInputChange({ target: { name: 'handoverDate', value: e.value } })}
                showIcon
                dateFormat="dd/mm/yy"
                className="w-100"
              />
            </div>
          </div>

          <div className="col-md-6">
            <div className="mb-3">
              <label htmlFor="status" className="form-label fw-medium">Status</label>
              <Dropdown
                id="status"
                value={handoverData.status}
                options={[
                  { label: 'Draft', value: 'Draft' },
                  { label: 'Completed', value: 'Completed' }
                ]}
                onChange={(e) => handleInputChange({ target: { name: 'status', value: e.value } })}
                className="w-100"
              />
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-light border border-light rounded">
          <h6 className="fw-medium mb-3 d-flex align-items-center">
            <i className="ti ti-link text-primary me-2"></i>
            Move-out Type
          </h6>
          <div className="d-flex flex-wrap gap-4 mb-3">
            <div className="d-flex align-items-center">
              <RadioButton
                inputId="withMoveIn"
                name="moveInOption"
                value={true}
                onChange={(e) => setWithMoveIn(e.value)}
                checked={withMoveIn}
              />
              <label htmlFor="withMoveIn" className="ms-2 fw-medium">With Move-In Reference</label>
            </div>
            <div className="d-flex align-items-center">
              <RadioButton
                inputId="withoutMoveIn"
                name="moveInOption"
                value={false}
                onChange={(e) => setWithMoveIn(e.value)}
                checked={!withMoveIn}
              />
              <label htmlFor="withoutMoveIn" className="ms-2 fw-medium">Without Move-In Reference</label>
            </div>
          </div>

          {withMoveIn && (
            <div className="mb-3">
              <label htmlFor="moveInHandover" className="form-label fw-medium">Move-In Handover *</label>
              <Dropdown
                id="moveInHandover"
                value={handoverData.relatedHandoverId}
                options={moveInHandovers
                  .filter(h => !handoverData.unitId || h.unitId?._id === handoverData.unitId)
                  .map(handover => ({
                    label: `${handover.unitId?.name || 'Unknown'} - ${new Date(handover.handoverDate).toLocaleDateString()}`,
                    value: handover._id
                  }))}
                onChange={(e) => setHandoverData(prev => ({ ...prev, relatedHandoverId: e.value }))}
                placeholder="Select a move-in handover"
                disabled={!handoverData.unitId}
                className="w-100"
              />
            </div>
          )}
        </div>

        <div className="row mt-3">
          <div className="col-md-6">
            <div className="mb-3">
              <label htmlFor="keysHandedOver" className="form-label fw-medium">Number of Keys</label>
              <InputNumber
                id="keysHandedOver"
                value={handoverData.keysHandedOver}
                onValueChange={(e) => handleInputChange({ target: { name: 'keysHandedOver', value: e.value } })}
                min={0}
                showButtons
                className="w-100"
              />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );


  const renderInventoryTab = () => (
    <Card className="mb-4">
      <div className="card-title mb-0 p-3 bg-light d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <i className="ti ti-package text-orange me-2" style={{ fontSize: '1.1rem' }}></i>
          <h5 className="mb-0">Inventory Items ({handoverData.items?.length || 0})</h5>
        </div>
        {handoverData.unitId && (
          <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
            <a
              href={`/facility/unit_management/view_unit/${handoverData.unitId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-primary btn-sm"
            >
              <i className="ti ti-external-link me-1"></i>
              Manage Inspection Items
            </a>
          </div>
        )}
      </div>
      <Divider className="m-0" />
      <div className="p-4">
        {handoverData.items.length === 0 && handoverData.unitId && (
          <div className="alert alert-info mb-3">
            <i className="ti ti-info-circle me-2"></i>
            No inspection items found for this unit. Please{' '}
            <a
              href={`/facility/unit_management/view_unit/${handoverData.unitId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="alert-link"
            >
              add inspection items in Unit Management
            </a>{' '}
            before creating the move-out handover.
          </div>
        )}
        <DataTable
          value={handoverData.items}
          emptyMessage="No items added yet. Select a unit to load inspection items."
          responsiveLayout="scroll"
          className="mb-4"
          paginator
          rows={5}
          rowsPerPageOptions={[5, 10, 15]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords} items"
        >
          <Column field="name" header="Name" />
          <Column field="category" header="Category" />
          <Column
            header="Source"
            body={(rowData) => (
              <Tag
                value={rowData.isFromMoveIn ? "From Move-In" : "Additional"}
                severity={rowData.isFromMoveIn ? "info" : "warning"}
                icon={rowData.isFromMoveIn ? "pi pi-check-circle" : "pi pi-plus-circle"}
              />
            )}
          />
          <Column
            header="Move-In Condition"
            body={(rowData) => (
              <Tag
                value={rowData.originalCondition || rowData.condition}
                severity={
                  ['Excellent', 'Good'].includes(rowData.originalCondition || rowData.condition) ? 'success' :
                    (rowData.originalCondition || rowData.condition) === 'Fair' ? 'warning' : 'danger'
                }
              />
            )}
          />
          <Column
            header="Current Condition"
            body={(rowData) => (
              <Tag
                value={rowData.currentCondition || rowData.condition}
                severity={
                  ['Excellent', 'Good'].includes(rowData.currentCondition || rowData.condition) ? 'success' :
                    (rowData.currentCondition || rowData.condition) === 'Fair' ? 'warning' : 'danger'
                }
              />
            )}
          />
          <Column field="quantity" header="Quantity" />
          <Column
            header="Repair Cost"
            body={(rowData) => {
              // Use item's specific currency, fallback to default
              let itemCurrency = defaultCurrency;

              if (rowData.currencyId) {
                if (typeof rowData.currencyId === 'object' && rowData.currencyId !== null && rowData.currencyId.currencyShortCode) {
                  itemCurrency = rowData.currencyId;
                } else if (typeof rowData.currencyId === 'string') {
                  const foundCurrency = currencies.find(curr => curr._id === rowData.currencyId);
                  if (foundCurrency) {
                    itemCurrency = foundCurrency;
                  }
                }
              } else if (rowData.currency && rowData.currency.currencyShortCode) {
                itemCurrency = rowData.currency;
              }

              const currencyCode = itemCurrency?.currencyShortCode || '$';
              const repairCost = rowData.repairCost || 0;

              if (repairCost > 0) {
                return (
                  <span className="fw-medium text-danger">
                    {currencyCode} {repairCost.toFixed(2)}
                  </span>
                );
              } else {
                return (
                  <span className="text-muted">
                    No repair cost
                  </span>
                );
              }
            }}
          />
          <Column
            header="Images"
            body={(rowData) => (
              <div>
                {rowData.images && rowData.images.length > 0 ? (
                  <Button
                    label={`${rowData.images.length}`}
                    icon="ti ti-photo"
                    className="p-button-sm p-button-info"
                    onClick={() => {
                      setSelectedItemImages(rowData.images);
                      setSelectedItemName(rowData.name);
                      setShowImageDialog(true);
                    }}
                    tooltip="View images"
                  />
                ) : (
                  <Tag value="No images" severity="secondary" />
                )}
              </div>
            )}
          />
          <Column
            body={(rowData, options) => (
              <div className="d-flex align-items-center" style={{ gap: '0.25rem' }}>
                <Button
                  icon="ti ti-edit"
                  className="p-button-rounded p-button-text p-button-info p-button-sm"
                  onClick={() => {
                    setTempItem({
                      ...rowData,
                      images: rowData.images || [],
                      currencyId: rowData.currencyId || rowData.currency?._id || defaultCurrency?._id,
                      currency: rowData.currency || (rowData.currencyId ? currencies.find(c => c._id === rowData.currencyId) : null) || defaultCurrency
                    });
                    setEditingItem(options.rowIndex);
                    setShowItemDialog(true);
                  }}
                  tooltip="Edit item"
                />
                <Button
                  icon="ti ti-trash"
                  className="p-button-rounded p-button-text p-button-danger p-button-sm"
                  onClick={() => {
                    const itemToRemove = handoverData.items[options.rowIndex];
                    const updatedItems = [...handoverData.items];
                    updatedItems.splice(options.rowIndex, 1);

                    // Remove associated deduction if exists
                    if (itemToRemove.repairCost && itemToRemove.repairCost > 0) {
                      const deductionReason = `${itemToRemove.name} - ${itemToRemove.condition === 'Damaged' ? 'Damage' :
                        itemToRemove.condition === 'Non-functional' ? 'Replacement' : 'Repair'}`;
                      const updatedDeductions = handoverData.securityDeposit.deductions.filter(d =>
                        d.reason !== deductionReason || d.amount !== itemToRemove.repairCost
                      );
                      setHandoverData(prev => ({
                        ...prev,
                        items: updatedItems,
                        securityDeposit: { ...prev.securityDeposit, deductions: updatedDeductions }
                      }));
                    } else {
                      setHandoverData(prev => ({ ...prev, items: updatedItems }));
                    }

                    toast.current.show({
                      severity: 'info',
                      summary: 'Item Removed',
                      detail: 'Inventory item removed successfully' + (itemToRemove.repairCost > 0 ? '. Associated deduction also removed.' : ''),
                      life: 3000
                    });
                  }}
                  tooltip="Remove item"
                />
              </div>
            )}
            header="Actions"
          />
        </DataTable>

        <div className="mt-4">
          <h6 className="fw-medium mb-3">Additional Notes</h6>
          <InputTextarea
            value={handoverData.notes}
            onChange={(e) => setHandoverData(prev => ({ ...prev, notes: e.target.value }))}
            rows={4}
            className="w-100"
            placeholder="Enter any additional notes about the move-out handover..."
          />
        </div>
      </div>
    </Card>
  );

  const renderMeterReadingsTab = () => (
    <Card className="mb-4">
      <div className="card-title mb-0 p-3 bg-light d-flex align-items-center">
        <i className="ti ti-gauge text-purple me-2" style={{ fontSize: '1.1rem' }}></i>
        <h5 className="mb-0">Meter Readings</h5>
      </div>
      <Divider className="m-0" />
      <div className="p-4">
        <div className="row">
          <div className="col-md-4">
            <label className="fw-medium mb-2">Electricity Reading</label>
            <div className="d-flex gap-2">
              <InputNumber
                value={handoverData.meterReadings.electricity.reading}
                onValueChange={(e) => handleMeterReadingChange('electricity', 'reading', e.value || 0)}
                min={0}
                showButtons
                className="w-100"
              />
              {powerMeterData && (
                <Button
                  icon="ti ti-refresh"
                  className="p-button-outlined p-button-sm"
                  tooltip="Fetch Latest Reading"
                  onClick={() => fetchPowerMeterData(handoverData.unitId)}
                  disabled={!handoverData.unitId || meterDataLoading}
                />
              )}
            </div>
            {powerMeterData && (
              <small className="text-success">
                Smart meter: {powerMeterData.meterNumber || 'Connected'}
              </small>
            )}
            {previousReadings.electricity.length > 0 && (
              <div className="mt-2">
                <small className="text-muted d-block">Previous readings:</small>
                {previousReadings.electricity.slice(0, 3).map((reading, index) => (
                  <small key={index} className="text-muted d-block">
                    {reading.reading} kWh ({new Date(reading.date).toLocaleDateString()})
                  </small>
                ))}
              </div>
            )}
          </div>
          <div className="col-md-4">
            <label className="fw-medium mb-2">Water Reading</label>
            <div className="d-flex gap-2">
              <InputNumber
                value={handoverData.meterReadings.water.reading}
                onValueChange={(e) => handleMeterReadingChange('water', 'reading', e.value || 0)}
                min={0}
                showButtons
                className="w-100"
              />
              {waterMeterData && (
                <Button
                  icon="ti ti-refresh"
                  className="p-button-outlined p-button-sm"
                  tooltip="Fetch Latest Reading"
                  onClick={() => fetchWaterMeterData(handoverData.unitId)}
                  disabled={!handoverData.unitId || meterDataLoading}
                />
              )}
            </div>
            {waterMeterData && (
              <small className="text-success">
                Smart meter: {waterMeterData.meterNumber}
              </small>
            )}
            {previousReadings.water.length > 0 && (
              <div className="mt-2">
                <small className="text-muted d-block">Previous readings:</small>
                {previousReadings.water.slice(0, 3).map((reading, index) => (
                  <small key={index} className="text-muted d-block">
                    {reading.reading} m³ ({new Date(reading.date).toLocaleDateString()})
                  </small>
                ))}
              </div>
            )}
          </div>
          <div className="col-md-4">
            <label className="fw-medium mb-2">Gas Reading</label>
            <InputNumber
              value={handoverData.meterReadings.gas.reading}
              onValueChange={(e) => handleMeterReadingChange('gas', 'reading', e.value || 0)}
              min={0}
              showButtons
              className="w-100"
            />
            <small className="text-muted">Manual entry only</small>
          </div>
        </div>

        {/* Previous Readings Section */}
        {(previousReadings.water.length > 0 || previousReadings.electricity.length > 0) && (
          <div className="mt-4">
            <h6 className="fw-medium mb-3">Previous Readings</h6>
            <div className="row">
              {previousReadings.electricity.length > 0 && (
                <div className="col-md-6">
                  <h6 className="fw-medium mb-2">Electricity</h6>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Reading</th>
                          <th>Date</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previousReadings.electricity.map((reading, index) => (
                          <tr key={index}>
                            <td>{reading.reading} kWh</td>
                            <td>{new Date(reading.date).toLocaleDateString()}</td>
                            <td>
                              <Tag
                                severity={reading.type.toLowerCase().includes('in') ? 'success' : 'info'}
                                value={reading.type}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {previousReadings.water.length > 0 && (
                <div className="col-md-6">
                  <h6 className="fw-medium mb-2">Water</h6>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Reading</th>
                          <th>Date</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previousReadings.water.map((reading, index) => (
                          <tr key={index}>
                            <td>{reading.reading} m³</td>
                            <td>{new Date(reading.date).toLocaleDateString()}</td>
                            <td>
                              <Tag
                                severity={reading.type.toLowerCase().includes('in') ? 'success' : 'info'}
                                value={reading.type}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );

  const renderAttachmentsTab = () => (
    <Card className="mb-4">
      <div className="card-title mb-0 p-3 bg-light d-flex align-items-center">
        <i className="ti ti-paperclip text-blue me-2" style={{ fontSize: '1.1rem' }}></i>
        <h5 className="mb-0">Attachments ({handoverData.attachments?.length || 0})</h5>
      </div>
      <Divider className="m-0" />
      <div className="p-4">
        <FileUpload
          name="documents"
          multiple
          accept="image/*,application/pdf"
          maxFileSize={5000000}
          customUpload={true}
          uploadHandler={handleFileUpload}
          chooseLabel="Choose Files"
          uploadLabel="Upload"
          cancelLabel="Cancel"
          className="mb-4"
          emptyTemplate={
            <div className="text-center p-6">
              <i className="ti ti-cloud-upload text-6xl text-400 mb-3"></i>
              <p className="text-600 mb-2 font-semibold">Drag and drop files here</p>
              <small className="text-500">Supports: Images, PDF (Max 5MB each)</small>
            </div>
          }
        />

        {handoverData.attachments?.length > 0 && (
          <div className="mt-4">
            <h6 className="fw-medium mb-3">Uploaded Files</h6>
            {handoverData.attachments.map((attachment, index) => (
              <div key={index} className="border border-light rounded p-3 mb-3">
                <div className="row align-items-center mb-2">
                  <div className="col-md-6">
                    <label htmlFor={`attachment-name-${index}`} className="form-label fw-medium mb-1">
                      Attachment Name *
                    </label>
                    <InputText
                      id={`attachment-name-${index}`}
                      value={attachment.name || ''}
                      onChange={(e) => updateAttachmentName(index, e.target.value)}
                      placeholder="e.g., Chair Image 23, Kitchen Photo"
                      className="w-100"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium mb-1">File</label>
                    <div className="d-flex align-items-center">
                      <i className={`${attachment.fileName?.endsWith('.pdf') ? 'ti ti-file-type-pdf' : 'ti ti-photo'} me-2 fs-4`}></i>
                      <div className="flex-grow-1">
                        <div className="text-muted small">{attachment.fileName}</div>
                        <small className="text-muted">
                          {attachment.file?.size ? `${(attachment.file.size / 1024).toFixed(2)} KB` : 'File uploaded'}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-end" style={{ gap: '0.5rem' }}>
                  {attachment.fileUrl && (
                    <a
                      href={attachment.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                      title="View file"
                    >
                      <i className="ti ti-eye me-1"></i>
                      View
                    </a>
                  )}
                  <Button
                    label="Remove"
                    icon="ti ti-trash"
                    className="p-button-sm p-button-danger p-button-outlined"
                    onClick={() => removeAttachment(index)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );

  const renderFinancialTab = () => {
    // Calculate refund amount automatically
    const totalDeductions = handoverData.securityDeposit.deductions.reduce(
      (total, deduction) => total + (parseFloat(deduction.amount) || 0), 0
    );
    const refundAmount = Math.max(0, (handoverData.securityDeposit.amount || 0) - totalDeductions);

    // Update refund amount in real time
    if (handoverData.securityDeposit.refundAmount !== refundAmount) {
      setHandoverData(prev => ({
        ...prev,
        securityDeposit: { ...prev.securityDeposit, refundAmount }
      }));
    }

    return (
      <Card className="mb-4">
        <div className="card-title mb-0 p-3 bg-light d-flex align-items-center">
          <i className="ti ti-currency-dollar text-success me-2" style={{ fontSize: '1.1rem' }}></i>
          <h5 className="mb-0">Security Deposit & Deductions</h5>
        </div>
        <Divider className="m-0" />
        <div className="p-4">
          <div className="row">
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label fw-medium">Deposit Amount</label>
                <InputNumber
                  value={handoverData.securityDeposit.amount}
                  onValueChange={(e) => setHandoverData(prev => ({
                    ...prev,
                    securityDeposit: { ...prev.securityDeposit, amount: e.value || 0 }
                  }))}
                  mode="decimal"
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  min={0}
                  className="w-100"
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label fw-medium">Total Deductions</label>
                <InputNumber
                  value={totalDeductions}
                  disabled
                  mode="decimal"
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  className="w-100"
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label fw-medium">Refund Amount</label>
                <InputNumber
                  value={refundAmount}
                  disabled
                  mode="decimal"
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  className="w-100"
                />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-medium mb-0 d-flex align-items-center">
                  <i className="ti ti-minus text-danger me-2"></i>
                  Deductions ({handoverData.securityDeposit.deductions?.length || 0})
                </h6>
                <Button
                  label="Add Deduction"
                  icon="ti ti-plus"
                  className="p-button-danger p-button-sm"
                  onClick={() => {
                    setTempDeduction({
                      reason: "",
                      amount: 0,
                      description: "",
                      currencyId: defaultCurrency?._id || null,
                      currency: defaultCurrency || null
                    });
                    setShowDeductionDialog(true);
                  }}
                />
              </div>
              <DataTable
                value={handoverData.securityDeposit.deductions}
                emptyMessage="No deductions added yet"
                responsiveLayout="scroll"
                className="mb-4"
              >
                <Column field="reason" header="Reason" />
                <Column
                  field="amount"
                  header="Amount"
                  body={(rowData) => (
                    <Tag 
                      value={`${rowData.currency?.currencyShortCode || defaultCurrency?.currencyShortCode || '$'} ${parseFloat(rowData.amount || 0).toFixed(2)}`}
                      severity="danger"
                    />
                  )}
                />
                <Column field="description" header="Description" />
                <Column
                  body={(rowData, options) => (
                    <div className="d-flex align-items-center" style={{ gap: '0.25rem' }}>
                      <Button
                        icon="ti ti-edit"
                        className="p-button-rounded p-button-text p-button-info p-button-sm"
                        onClick={() => {
                          setTempDeduction({
                            ...rowData,
                            currencyId: rowData.currencyId || rowData.currency?._id || defaultCurrency?._id || null,
                            currency: rowData.currency || defaultCurrency || null
                          });
                          setShowDeductionDialog(true);
                        }}
                        tooltip="Edit deduction"
                      />
                      <Button
                        icon="ti ti-trash"
                        className="p-button-rounded p-button-text p-button-danger p-button-sm"
                        onClick={() => {
                          const updatedDeductions = [...handoverData.securityDeposit.deductions];
                          updatedDeductions.splice(options.rowIndex, 1);
                          setHandoverData(prev => ({
                            ...prev,
                            securityDeposit: { ...prev.securityDeposit, deductions: updatedDeductions }
                          }));
                          toast.current.show({
                            severity: 'info',
                            summary: 'Deduction Removed',
                            detail: 'Deduction removed successfully',
                            life: 3000
                          });
                        }}
                        tooltip="Remove deduction"
                      />
                    </div>
                  )}
                  header="Actions"
                />
              </DataTable>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  // Comprehensive Item Dialog Component
  const renderItemDialog = () => {
    const handleItemImageUpload = (event) => {
      const files = Array.from(event.files);
      const imageData = files.map(file => ({
        file: file,
        preview: URL.createObjectURL(file),
        name: file.name,
        size: file.size
      }));
      setTempItem(prev => ({
        ...prev,
        images: [...(prev.images || []), ...imageData]
      }));
      event.options.clear();
    };

    const removeItemImage = (index) => {
      setTempItem(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    };

    const handleSaveItem = () => {
      if (!tempItem.name || !tempItem.category) {
        toast.current.show({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please fill in all required fields',
          life: 3000
        });
        return;
      }

      // Prepare item for UI state (keeps images for display)
      const itemForUI = {
        ...tempItem,
        currencyId: tempItem.currencyId || defaultCurrency?._id,
        currency: tempItem.currency || defaultCurrency
      };

      // Prepare item for database (excludes images temporarily)
      const itemForDB = {
        ...itemForUI,
        // For now, exclude images from database save to prevent validation errors
        // TODO: Implement proper file upload to server and store actual URLs
        images: []
      };

      // Remove the manual ID assignment - let MongoDB generate proper ObjectIds
      delete itemForDB.id;

      // Handle repair cost deductions
      const handleRepairDeduction = (item, isEdit = false, oldItem = null) => {
        const repairCost = parseFloat(item.repairCost) || 0;
        const oldRepairCost = oldItem ? parseFloat(oldItem.repairCost) || 0 : 0;

        if (repairCost > 0) {
          const deductionReason = `${item.name} - ${item.condition === 'Damaged' ? 'Damage' :
            item.condition === 'Non-functional' ? 'Replacement' : 'Repair'}`;

          // Remove old deduction if editing
          if (isEdit && oldRepairCost > 0) {
            const oldDeductionReason = `${oldItem.name} - ${oldItem.condition === 'Damaged' ? 'Damage' :
              oldItem.condition === 'Non-functional' ? 'Replacement' : 'Repair'}`;
            const updatedDeductions = handoverData.securityDeposit.deductions.filter(d =>
              d.reason !== oldDeductionReason || d.amount !== oldRepairCost
            );
            setHandoverData(prev => ({
              ...prev,
              securityDeposit: { ...prev.securityDeposit, deductions: updatedDeductions }
            }));
          }

          // Add new deduction
          const newDeduction = {
            reason: deductionReason,
            amount: repairCost,
            description: `Repair/replacement cost for ${item.name} (${item.condition} condition)`
          };

          setHandoverData(prev => ({
            ...prev,
            securityDeposit: {
              ...prev.securityDeposit,
              deductions: [...prev.securityDeposit.deductions, newDeduction]
            }
          }));
        } else if (isEdit && oldRepairCost > 0) {
          // Remove old deduction if repair cost was removed
          const oldDeductionReason = `${oldItem.name} - ${oldItem.condition === 'Damaged' ? 'Damage' :
            oldItem.condition === 'Non-functional' ? 'Replacement' : 'Repair'}`;
          const updatedDeductions = handoverData.securityDeposit.deductions.filter(d =>
            d.reason !== oldDeductionReason || d.amount !== oldRepairCost
          );
          setHandoverData(prev => ({
            ...prev,
            securityDeposit: { ...prev.securityDeposit, deductions: updatedDeductions }
          }));
        }
      };

      if (editingItem !== null) {
        // Edit existing item
        const updatedItems = [...handoverData.items];
        const oldItem = updatedItems[editingItem];
        updatedItems[editingItem] = { ...itemForUI };
        setHandoverData(prev => ({ ...prev, items: updatedItems }));
        handleRepairDeduction(itemForDB, true, oldItem);
      } else {
        // Add new item
        setHandoverData(prev => ({
          ...prev,
          items: [...prev.items, { ...itemForUI }]
        }));
        handleRepairDeduction(itemForDB);
      }

      setShowItemDialog(false);
      setTempItem({
        name: "",
        category: "",
        description: "",
        condition: "Good",
        quantity: 1,
        serialNumber: "",
        notes: "",
        images: [],
        repairCost: 0,
        requiresRepair: false
      });
      setEditingItem(null);

      toast.current.show({
        severity: 'success',
        summary: editingItem !== null ? 'Item Updated' : 'Item Added',
        detail: `Inventory item ${editingItem !== null ? 'updated' : 'added'} successfully${tempItem.repairCost > 0 ? '. Repair cost added to deductions.' : ''}`,
        life: 3000
      });
    };

    return (
      <Dialog
        header={editingItem !== null ? "Edit Inventory Item" : "Add Inventory Item"}
        visible={showItemDialog}
        style={{ width: '70vw', maxWidth: '800px' }}
        onHide={() => {
          setShowItemDialog(false);
          setTempItem({
            name: "",
            category: "",
            description: "",
            condition: "Good",
            quantity: 1,
            serialNumber: "",
            notes: "",
            images: [],
            repairCost: 0,
            requiresRepair: false,
            currencyId: defaultCurrency?._id,
            currency: defaultCurrency
          });
          setEditingItem(null);
        }}
        modal
      >
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="fw-medium mb-2">Item Name *</label>
              <InputText
                value={tempItem.name}
                onChange={(e) => setTempItem(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter item name"
                className="w-100"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="fw-medium mb-2">Category *</label>
              <Dropdown
                value={tempItem.category}
                options={categoryOptions}
                onChange={(e) => setTempItem(prev => ({ ...prev, category: e.value }))}
                placeholder="Select category"
                className="w-100"
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="fw-medium mb-2">Condition *</label>
              <Dropdown
                value={tempItem.condition}
                options={conditionOptions}
                onChange={(e) => {
                  const newCondition = e.value;
                  const resetRepairCost = ['Excellent', 'Good'].includes(newCondition);
                  setTempItem(prev => ({
                    ...prev,
                    condition: newCondition,
                    repairCost: resetRepairCost ? 0 : prev.repairCost,
                    requiresRepair: resetRepairCost ? false : prev.requiresRepair
                  }));
                }}
                className="w-100"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="fw-medium mb-2">Quantity</label>
              <InputNumber
                value={tempItem.quantity}
                onValueChange={(e) => setTempItem(prev => ({ ...prev, quantity: e.value || 1 }))}
                min={1}
                showButtons
                className="w-100"
              />
            </div>
          </div>
        </div>

        <div className="mb-3">
          <label className="fw-medium mb-2">Description</label>
          <InputTextarea
            value={tempItem.description}
            onChange={(e) => setTempItem(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
            className="w-100"
            placeholder="Enter item description..."
          />
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="fw-medium mb-2">Serial Number</label>
              <InputText
                value={tempItem.serialNumber}
                onChange={(e) => setTempItem(prev => ({ ...prev, serialNumber: e.target.value }))}
                placeholder="Enter serial number (if applicable)"
                className="w-100"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="fw-medium mb-2">Additional Notes</label>
              <InputText
                value={tempItem.notes}
                onChange={(e) => setTempItem(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                className="w-100"
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="fw-medium mb-2">Currency</label>
              <Dropdown
                value={tempItem.currencyId || tempItem.currency?._id || defaultCurrency?._id}
                options={currencies.map(curr => ({
                  label: `${curr.currencyName} (${curr.currencyShortCode})`,
                  value: curr._id,
                  currency: curr
                }))}
                onChange={(e) => {
                  const selectedCurrency = currencies.find(curr => curr._id === e.value);
                  setTempItem(prev => ({
                    ...prev,
                    currencyId: e.value,
                    currency: selectedCurrency
                  }));
                }}
                placeholder="Select currency"
                className="w-100"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="fw-medium mb-2">Repair/Replacement Cost</label>
              <div className="p-inputgroup">
                <span className="p-inputgroup-addon">
                  {(() => {
                    let currency = defaultCurrency;
                    if (tempItem.currencyId) {
                      const found = currencies.find(curr => curr._id === tempItem.currencyId);
                      if (found) currency = found;
                    } else if (tempItem.currency) {
                      currency = tempItem.currency;
                    }
                    return currency?.currencyShortCode || '$';
                  })()}
                </span>
                <InputNumber
                  value={tempItem.repairCost}
                  onValueChange={(e) => {
                    const cost = e.value || 0;
                    setTempItem(prev => ({
                      ...prev,
                      repairCost: cost,
                      requiresRepair: cost > 0
                    }));
                  }}
                  mode="decimal"
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  min={0}
                  placeholder="0.00"
                  className="w-100"
                  disabled={['Excellent', 'Good'].includes(tempItem.condition)}
                />
              </div>
              <small className="text-muted">
                {['Excellent', 'Good'].includes(tempItem.condition)
                  ? 'No repair cost for items in excellent/good condition'
                  : 'Enter cost to repair or replace this item. This will be added to security deposit deductions.'}
              </small>
            </div>
          </div>
        </div>

        {tempItem.repairCost > 0 && (
          <div className="row">
            <div className="col-md-12">
              <div className="mb-3">
                <label className="fw-medium mb-2">Repair Description</label>
                <InputTextarea
                  value={tempItem.repairDescription || ''}
                  onChange={(e) => setTempItem(prev => ({ ...prev, repairDescription: e.target.value }))}
                  placeholder="Describe the repairs needed, damage found, or replacement required..."
                  className="w-100"
                  rows={3}
                />
                <small className="text-muted">
                  Describe what needs to be fixed or replaced to help with the deduction justification
                </small>
              </div>
            </div>
          </div>
        )}

        <div className="mb-3">
          <label className="fw-medium mb-2">Item Images</label>
          <FileUpload
            name="itemImages"
            multiple
            accept="image/*"
            maxFileSize={2000000}
            customUpload={true}
            uploadHandler={handleItemImageUpload}
            chooseLabel="Add Images"
            className="mb-3"
            emptyTemplate={
              <div className="text-center p-4">
                <i className="ti ti-camera text-4xl text-muted mb-2"></i>
                <p className="text-muted mb-1">Drag and drop images here</p>
                <small className="text-muted">Supports: JPG, PNG (Max 2MB each)</small>
              </div>
            }
          />

          {tempItem.images && tempItem.images.length > 0 && (
            <div>
              <label className="fw-medium mb-2 d-block">Current Images</label>
              <div className="d-flex flex-wrap" style={{ gap: '0.5rem' }}>
                {tempItem.images.map((imageData, index) => (
                  <div key={index} className="position-relative" style={{ width: '80px', height: '80px' }}>
                    <img
                      src={imageData.preview || imageData}
                      alt={`Item ${index + 1}`}
                      className="w-100 h-100 object-cover rounded border"
                      style={{ objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div
                      className="w-100 h-100 d-flex align-items-center justify-content-center rounded border bg-light text-muted"
                      style={{ display: 'none', fontSize: '0.75rem', textAlign: 'center' }}
                    >
                      <div>
                        <i className="ti ti-photo-off d-block mb-1"></i>
                        <small>Failed to load</small>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0"
                      style={{ transform: 'translate(50%, -50%)' }}
                      onClick={() => removeItemImage(index)}
                      title="Remove image"
                    >
                      <i className="ti ti-x" style={{ fontSize: '0.7rem' }}></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="d-flex justify-content-end align-items-center mt-4" style={{ gap: '0.5rem' }}>
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => {
              setShowItemDialog(false);
              setTempItem({
                name: "",
                category: "",
                description: "",
                condition: "Good",
                quantity: 1,
                serialNumber: "",
                notes: "",
                images: [],
                repairCost: 0,
                requiresRepair: false,
                currencyId: defaultCurrency?._id,
                currency: defaultCurrency
              });
              setEditingItem(null);
            }}
          />
          <Button
            label={editingItem !== null ? "Update Item" : "Add Item"}
            className="p-button-success"
            onClick={handleSaveItem}
          />
        </div>
      </Dialog>
    );
  };

  // Deduction Dialog Component
  const renderDeductionDialog = () => {
    const handleSaveDeduction = () => {
      if (!tempDeduction.reason || !tempDeduction.amount || tempDeduction.amount <= 0) {
        toast.current.show({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please fill in all required fields with valid values',
          life: 3000
        });
        return;
      }

      const isEditing = handoverData.securityDeposit.deductions.some(d =>
        d.reason === tempDeduction.reason && d.amount === tempDeduction.amount && d.description === tempDeduction.description
      );
      const deductionCurrencyId = tempDeduction.currencyId || defaultCurrency?._id || null;
      const deductionCurrency = tempDeduction.currency || defaultCurrency || null;

      if (isEditing) {
        // Update existing deduction
        const updatedDeductions = handoverData.securityDeposit.deductions.map(deduction =>
          (deduction.reason === tempDeduction.reason && 
           deduction.amount === tempDeduction.amount && 
           deduction.description === tempDeduction.description) ? {
            ...tempDeduction,
            currencyId: deductionCurrencyId,
            currency: deductionCurrency
           } : deduction
        );
        setHandoverData(prev => ({
          ...prev,
          securityDeposit: { ...prev.securityDeposit, deductions: updatedDeductions }
        }));
      } else {
        // Add new deduction
        setHandoverData(prev => ({
          ...prev,
          securityDeposit: {
            ...prev.securityDeposit,
            deductions: [...prev.securityDeposit.deductions, { 
              ...tempDeduction,
              currencyId: deductionCurrencyId,
              currency: deductionCurrency
            }]
          }
        }));
      }

      setShowDeductionDialog(false);
      setTempDeduction({ reason: "", amount: 0, description: "", currencyId: null, currency: null });

      toast.current.show({
        severity: 'success',
        summary: isEditing ? 'Deduction Updated' : 'Deduction Added',
        detail: `Security deposit deduction ${isEditing ? 'updated' : 'added'} successfully`,
        life: 3000
      });
    };

    return (
      <Dialog
        header="Add Security Deposit Deduction"
        visible={showDeductionDialog}
        style={{ width: '50vw', maxWidth: '600px' }}
        onHide={() => setShowDeductionDialog(false)}
        modal
      >
        <div className="mb-3">
          <label className="fw-medium mb-2">Deduction Reason *</label>
          <InputText
            value={tempDeduction.reason}
            onChange={(e) => setTempDeduction(prev => ({ ...prev, reason: e.target.value }))}
            placeholder="Enter deduction reason (e.g., Damage to walls, Missing keys, etc.)"
            className="w-100"
          />
        </div>

        <div className="mb-3">
          <label className="fw-medium mb-2">
            Deduction Amount * {tempDeduction.currency?.currencyShortCode ? `(${tempDeduction.currency.currencyShortCode})` : ''}
          </label>
          <InputNumber
            value={tempDeduction.amount}
            onValueChange={(e) => setTempDeduction(prev => ({ ...prev, amount: e.value || 0 }))}
            mode="currency"
            currency={tempDeduction.currency?.currencyShortCode || defaultCurrency?.currencyShortCode || 'USD'}
            locale="en-US"
            minFractionDigits={2}
            maxFractionDigits={2}
            min={0}
            placeholder="0.00"
            className="w-100"
          />
        </div>

        <div className="mb-3">
          <label className="fw-medium mb-2">Currency *</label>
          <Dropdown
            value={tempDeduction.currencyId || defaultCurrency?._id}
            options={currencies.map(curr => ({
              label: `${curr.currencyName} (${curr.currencyShortCode})`,
              value: curr._id,
              currency: curr
            }))}
            onChange={(e) => {
              const selectedCurrency = currencies.find(curr => curr._id === e.value);
              setTempDeduction(prev => ({
                ...prev,
                currencyId: e.value,
                currency: selectedCurrency || defaultCurrency || null
              }));
            }}
            placeholder="Select currency"
            className="w-100"
          />
        </div>
        
        <div className="mb-3">
          <label className="fw-medium mb-2">Description</label>
          <InputTextarea
            value={tempDeduction.description}
            onChange={(e) => setTempDeduction(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
            className="w-100"
            placeholder="Enter detailed description of the deduction..."
          />
        </div>

        <div className="d-flex justify-content-end align-items-center mt-4" style={{ gap: '0.5rem' }}>
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => setShowDeductionDialog(false)}
          />
          <Button
            label="Add Deduction"
            className="p-button-danger"
            onClick={handleSaveDeduction}
          />
        </div>
      </Dialog>
    );
  };

  // File upload handler for attachments
  const handleFileUpload = (event) => {
    const files = event.files;
    if (!files || files.length === 0) return;

    const newAttachments = Array.from(files).map(file => ({
      id: Date.now() + '-' + Math.random().toString(36).substr(2, 9),
      name: '', // Custom name to be filled by user
      fileName: file.name, // Actual file name
      fileUrl: URL.createObjectURL(file),
      file: file,
      uploadDate: new Date().toISOString(),
      size: file.size
    }));

    setHandoverData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...newAttachments]
    }));

    event.options.clear();
    toast.current.show({
      severity: 'success',
      summary: 'Files Uploaded',
      detail: `${files.length} file(s) uploaded successfully. Please provide a name for each attachment.`,
      life: 4000
    });
  };

  // Update attachment name
  const updateAttachmentName = (index, name) => {
    setHandoverData(prev => {
      const newAttachments = [...prev.attachments];
      newAttachments[index] = {
        ...newAttachments[index],
        name: name
      };
      return { ...prev, attachments: newAttachments };
    });
  };

  const removeAttachment = (index) => {
    setHandoverData(prev => {
      const newAttachments = [...prev.attachments];
      newAttachments.splice(index, 1);
      return { ...prev, attachments: newAttachments };
    });

    toast.current.show({
      severity: 'info',
      summary: 'Attachment Removed',
      detail: 'Attachment removed successfully',
      life: 3000
    });
  };

  const content = (
    <>
      <Toast ref={toast} />

      {loading ? (
        <div className="d-flex align-items-center justify-content-center py-5">
          <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" fill="var(--surface-ground)" animationDuration=".5s" />
          <span className="ms-3 font-medium">Loading data...</span>
        </div>
      ) : (
        <div className="p-4">
          <div className="max-w-6xl mx-auto">
            {/* Enhanced Header */}
            {renderHandoverHeader()}

            {/* Enhanced Tab Interface */}
            <TabView
              activeIndex={activeTab}
              onTabChange={(e) => setActiveTab(e.index)}
              className="custom-tabview"
            >
              <TabPanel
                header={
                  <div className="d-flex align-items-center">
                    <i className="ti ti-info-circle me-2"></i>
                    Basic Info
                  </div>
                }
              >
                {renderBasicInfoTab()}
              </TabPanel>
              <TabPanel
                header={
                  <div className="d-flex align-items-center">
                    <i className="ti ti-gauge me-2"></i>
                    Meter Readings
                  </div>
                }
              >
                {renderMeterReadingsTab()}
              </TabPanel>
              <TabPanel
                header={
                  <div className="d-flex align-items-center">
                    <i className="ti ti-package me-2"></i>
                    Inventory ({handoverData.items?.length || 0})
                  </div>
                }
              >
                {renderInventoryTab()}
              </TabPanel>
              <TabPanel
                header={
                  <div className="d-flex align-items-center">
                    <i className="ti ti-paperclip me-2"></i>
                    Attachments ({handoverData.attachments?.length || 0})
                  </div>
                }
              >
                {renderAttachmentsTab()}
              </TabPanel>
              <TabPanel
                header={
                  <div className="d-flex align-items-center">
                    <i className="ti ti-currency-dollar me-2"></i>
                    Financial
                  </div>
                }
              >
                {renderFinancialTab()}
              </TabPanel>
            </TabView>

            {/* Action Buttons */}
            <div className="d-flex justify-content-end align-items-center mt-4" style={{ gap: '0.75rem' }}>
              <Button
                label="Clear Form"
                icon="ti ti-eraser"
                onClick={handleClear}
                className="p-button-outlined p-button-secondary"
                disabled={isSubmitting}
              />
              <Button
                label={isSubmitting ? "Creating..." : "Create Move-Out Handover"}
                icon="ti ti-check"
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
                className="p-button-primary"
              />
            </div>
          </div>
        </div>
      )}

      {/* Dialogs */}
      {renderItemDialog()}
      {renderDeductionDialog()}

      {/* Inspection Item Dialog */}
      {showInspectionTab && (
        <HandoverInspectionTab
          facilityId={facilityId}
          unitId={handoverData.unitId}
          showListView={false}
          autoOpenModal={true}
          context="moveOut"
          onItemAdded={(addedItem) => {
            // Mark as NOT from move-in (added during move-out)
            const itemWithFlag = {
              ...addedItem,
              isFromMoveIn: false,
              moveInHandoverId: null,
              moveInItemId: null
            };

            // Add item to local handover inventory
            setHandoverData(prev => ({
              ...prev,
              items: [...prev.items, itemWithFlag]
            }));

            // Handle repair cost deduction ONLY if item is from move-in
            // New items added during move-out cannot be charged for damage
            if (addedItem.repairCost > 0 && itemWithFlag.isFromMoveIn) {
              const deductionReason = `${addedItem.name} - ${addedItem.condition === 'Damaged' ? 'Damage' :
                addedItem.condition === 'Non-functional' ? 'Replacement' : 'Repair'}`;

              const newDeduction = {
                reason: deductionReason,
                amount: addedItem.repairCost,
                description: addedItem.repairDescription || `Repair/replacement cost for ${addedItem.name} (${addedItem.condition} condition)`
              };

              setHandoverData(prev => ({
                ...prev,
                securityDeposit: {
                  ...prev.securityDeposit,
                  deductions: [...prev.securityDeposit.deductions, newDeduction]
                }
              }));
            } else if (addedItem.repairCost > 0 && !itemWithFlag.isFromMoveIn) {
              // Show warning that new items can't be charged
              toast.current.show({
                severity: 'warn',
                summary: 'Cannot Charge for New Item',
                detail: `${addedItem.name} was not in the original move-in handover. Damage charges cannot be applied to new items.`,
                life: 5000
              });
            }

            // Close the dialog
            setShowInspectionTab(false);

            // Show success message
            toast.current.show({
              severity: 'success',
              summary: 'Item Added',
              detail: `${addedItem.name} added to inventory${addedItem.repairCost > 0 ? '. Repair cost added to deductions.' : ''}`,
              life: 3000
            });
          }}
          onClose={() => {
            setShowInspectionTab(false);
            // Reload unit inspection items if needed
            if (handoverData.unitId) {
              // Could add refresh logic here if needed
            }
          }}
        />
      )}

      {/* Image Viewer Dialog */}
      <Dialog
        header={`Images - ${selectedItemName}`}
        visible={showImageDialog}
        style={{ width: '80vw', maxWidth: '900px' }}
        onHide={() => setShowImageDialog(false)}
        dismissableMask
      >
        <div className="row g-3">
          {selectedItemImages.length > 0 ? (
            selectedItemImages.map((imageUrl, index) => (
              <div key={index} className="col-md-6">
                <div className="card">
                  <img
                    src={`${backend_url}${imageUrl}`}
                    alt={`${selectedItemName} - Image ${index + 1}`}
                    style={{ width: '100%', height: 'auto', maxHeight: '400px', objectFit: 'contain' }}
                    className="card-img-top"
                  />
                  <div className="card-body">
                    <small className="text-muted">Image {index + 1}</small>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12 text-center py-4">
              <p className="text-muted">No images available</p>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );

  if (isDialog) {
    return content;
  }

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/app/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/handover_management">Handover Management</Link>
                </li>
                <li className="breadcrumb-item">Create Move-Out Handover</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <Link to="#" onClick={() => navigate(-1)}>
                <i className="ti ti-arrow-narrow-left"></i> Back
              </Link>
            </div>
          </div>

          {content}
        </div>
      </div>
    </Layout>
  );
};

export default CreateMoveOutHandover;
